import React, { useMemo } from "react";
import {
  makeStyles,
  Box,
  Typography,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TablePagination,
  IconButton,
  TableRow,
} from "@material-ui/core";
import { StyledTableCell, StyledTableRow } from "components/table";
import { SkeletonTableRow } from "components/skeleton/skeleton.component";
import { TablePaginationActions } from "components/tablePagination";
import RefreshTable from "components/table/refreshTable";
import { getLang } from "app/feature/constants";
import StickyLeftTableCell from "components/table/stickyLeftTableCell";
import StickyRightTableCell from "components/table/stickyRightTableCell";
import DownloadIcon from "@material-ui/icons/GetAppRounded";
import moment from "moment";
import "moment/min/locales.min";
import { currencies } from "lib/constants/currency";

const useStyle = makeStyles((theme) => ({
  tableContainer: {
    border: "1px solid #F2F4F7",
    width: "100%",
    borderRadius: 8,
  },
  table: {
    tableLayout: "auto",
    whiteSpace: "nowrap",
    borderCollapse: "separate",
    width: "100%",
    [theme.breakpoints.down("xs")]: {
      minWidth: 0,
    },
    "& th,td": {
      fontSize: ".75rem",
      borderRight: "1px solid #F2F4F7",
      borderBottom: "1px solid #F2F4F7",
      padding: theme.spacing(1, 2, 1, 2),
    },
    "& th": {
      color: theme.palette.text.secondary,
      fontWeight: theme.typography.fontWeightBold,
    },
    "& th:last-child, td:last-child": {
      width: 37,
      borderRight: "0px",
    },
    "& tr:last-child > td": {
      borderBottom: "none",
    },
  },
  emptyContainer: {
    backgroundColor: theme.palette.background.paper,
    textAlign: "center",
  },
  button: {
    alignItems: "center",
    padding: 0,
    minWidth: 0,
    marginLeft: theme.spacing(1),
  },
  attributeText: {
    color: "#b2b2b2",
    fontSize: 12,
    whiteSpace: "break-spaces",
  },
  statusChip: {
    borderRadius: "16px",
    padding: "4px 12px",
    color: "#ffffff",
    fontWeight: 500,
  }
}));

export default function CampaignTngDisbursementTableComponent({
  rows,
  isFetching,
  isError,
  handleReload,
  handleChangePage,
  handleChangeRowsPerPage,
  totalFiltered,
  paginationOptions,
  tableFilter,
  lang,
  handleExport,
  language,
  disbursementInfo,
  campaignFilter = "all",
}) {
  const classes = useStyle();

  if (language === "ZH") {
    moment.locale("zh-cn");
  } else if (language === "TL") {
    moment.locale("tl-ph");
  } else {
    moment.locale(language);
  }

  const statusColor = {
    Published: {
      color: "#067647",
      background: "#ECFDF3",
      border: "#ABEFC6",
    },
    "Not Published": {
      color: "#FF9933",
      background: "#fff7ef",
      border: "#ffd0a1",
    },
    Ended: {
      color: "#475467",
      background: "#FAFAFA",
      border: "#E4E7EC",
    },
  };

  const getProgress = (row) => {
    const now = moment.utc();
    let progress = getLang(lang, "label.DRAFT");
    let color = "#B0B0B0";

    // Ended
    if (
      row.status === "Ended" ||
      (row.end_at && now.isAfter(moment.utc(row.end_at)))
    ) {
      progress = getLang(lang, "label.EVENT_CONCLUDED");
      color = "#3A4D54";
    }
    // Published
    else if (row.status === "Published") {
      // Instant Win
      if (row.type === "instant_win") {
        if (row.join_close_at && now.isAfter(moment.utc(row.join_close_at))) {
          progress = getLang(lang, "label.JOIN_PERIOD_CLOSED");
          color = "#FF1D1D";
        } else if (
          row.join_open_at &&
          now.isAfter(moment.utc(row.join_open_at))
        ) {
          progress = getLang(lang, "label.JOIN_PERIOD_OPEN");
          color = "#4BCB0F";
        } else if (
          row.pre_start_at &&
          now.isAfter(moment.utc(row.pre_start_at))
        ) {
          progress = getLang(lang, "label.AWAITING_START");
          color = "#FFB74D";
        } else {
          progress = getLang(lang, "label.AWAITING_START");
          color = "#FFB74D";
        }
      }
      // Instant Lucky Draw
      else if (row.type === "instant_lucky_draw") {
        if (row.draw_end_at && now.isAfter(moment.utc(row.draw_end_at))) {
          progress = getLang(lang, "label.LIVE_DRAW_COMPLETED");
          color = "#8E6BB3";
        } else if (
          row.draw_start_at &&
          now.isAfter(moment.utc(row.draw_start_at))
        ) {
          progress = getLang(lang, "label.LIVE_DRAW_IN_PROGRESS");
          color = "#8E6BB3";
        } else if (
          row.join_close_at &&
          now.isAfter(moment.utc(row.join_close_at))
        ) {
          progress = getLang(lang, "label.JOIN_PERIOD_CLOSED");
          color = "#FF1D1D";
        } else if (
          row.join_open_at &&
          now.isAfter(moment.utc(row.join_open_at))
        ) {
          progress = getLang(lang, "label.JOIN_PERIOD_OPEN");
          color = "#4BCB0F";
        } else if (
          row.pre_start_at &&
          now.isAfter(moment.utc(row.pre_start_at))
        ) {
          progress = getLang(lang, "label.AWAITING_START");
          color = "#FFB74D";
        } else {
          progress = getLang(lang, "label.AWAITING_START");
          color = "#FFB74D";
        }
      }
    }
    // Not Published
    else if (row.status === "Not Published") {
      progress = getLang(lang, "label.DRAFT");
      color = "#B0B0B0";
    }

    return (
      <Typography variant="caption" style={{ color }}>
        {progress}
      </Typography>
    );
  };

  const exp = useMemo(() => {
    if (disbursementInfo?.currency) {
      return (
        currencies.find((c) => c.alpha3 === disbursementInfo.currency)?.exp ?? 2
      );
    }
    return 2;
  }, [disbursementInfo]);

  const typeLabel = {
    instant_win: getLang(lang, "label.INSTANT_WIN"),
    instant_lucky_draw: getLang(lang, "label.INSTANT_LUCKY_DRAW"),
    lucky_draw: getLang(lang, "label.LUCKY_DRAW"),
  };

  const filteredRows = useMemo(() => {
    if (!rows) return [];
    switch (campaignFilter) {
      case "live":
        return rows.filter(
          (r) => r?.status === "Published" || r?.status === "Not Published"
        );
      case "past":
        return rows.filter((r) => r?.status === "Ended");
      default:
        return rows;
    }
  }, [rows, campaignFilter]);

  return (
    <>
      {!isFetching && isError && <RefreshTable handleReload={handleReload} />}
      {filteredRows?.length > 0 || isFetching ? (
        <Box>
          <TableContainer className={classes.tableContainer}>
            <Table
              className={classes.table}
              size="small"
              aria-label="assign table"
              stickyHeader
            >
              <TableHead>
                <TableRow>
                  <StickyLeftTableCell   
                    style={{
                        textAlign: "center",
                        position: "sticky",
                        left: 0,
                        zIndex: 130, 
                        background: "#fff", 
                      }}
                    >
                    <Box px={1}>{getLang(lang, "label.CAMPAIGN_NAME")}</Box>
                  </StickyLeftTableCell>
                  <StyledTableCell>
                    {getLang(lang, "label.TYPE")}
                  </StyledTableCell>
                  <StyledTableCell>
                    {getLang(lang, "label.STATUS")}
                  </StyledTableCell>
                  <StyledTableCell>
                    {getLang(lang, "label.PROGRESS")}
                  </StyledTableCell>
                  <StyledTableCell>
                    {getLang(lang, "label.START_DATE")}
                  </StyledTableCell>
                  <StyledTableCell>
                    {getLang(lang, "label.END_DATE")}
                  </StyledTableCell>
                  <StyledTableCell>
                    {getLang(lang, "label.ALLOCATED_CREDIT")}{" "}
                    {disbursementInfo ? `(${disbursementInfo.currency})` : ""}
                  </StyledTableCell>
                  <StyledTableCell>
                    {getLang(lang, "label.CLAIMED_BALANCE")}{" "}
                    {disbursementInfo ? `(${disbursementInfo.currency})` : ""}
                  </StyledTableCell>
                  <StyledTableCell>
                    {getLang(lang, "label.UNCLAIMED_BALANCE")}{" "}
                    {disbursementInfo ? `(${disbursementInfo.currency})` : ""}
                  </StyledTableCell>
                  <StyledTableCell>
                    {getLang(lang, "label.WIN_COUNT")}
                  </StyledTableCell>
                  <StyledTableCell>
                    {getLang(lang, "label.CLAIM_COUNT")}
                  </StyledTableCell>

                  <StickyRightTableCell style={{ textAlign: "center" }}>
                    <Box px={1}>{getLang(lang, "label.ACTION")}</Box>
                  </StickyRightTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isFetching
                  ? [...new Array(5)].map((v, index) => (
                      <SkeletonTableRow key={index} columnCount={8} />
                    ))
                  : filteredRows.map((row, index) => {
                      return (
                        <StyledTableRow
                          key={index}
                          role="checkbox"
                          tabIndex={-1}
                        >
                          <StickyLeftTableCell
                            style={{
                              textAlign: "center",
                              backgroundColor: "inherit",
                            }}
                          >
                            <Box
                              px={1}
                              display="flex"
                              justifyContent="flex-start"
                            >
                              {row.campaign_name}
                            </Box>
                          </StickyLeftTableCell>
                          <StyledTableCell>
                            <Typography variant="caption">
                              {typeLabel[row.type] ?? "-"}
                            </Typography>
                          </StyledTableCell>
                          <StyledTableCell>
                            <Box display="flex" justifyContent="center">
                              <Box
                                className={classes.statusChip}
                                style={{
                                  backgroundColor:
                                    statusColor[row.status]?.background ??
                                    "white",
                                  color:
                                    statusColor[row.status]?.color ?? "black",
                                  border: `1px solid ${
                                    statusColor[row.status]?.border ?? "#E4E7EC"
                                  }`,
                                }}
                              >
                                {row.status === "Ended"
                                  ? getLang(lang, "label.ENDED")
                                  : row.status === "Published"
                                  ? getLang(lang, "label.ACTIVE")
                                  : getLang(lang, "label.NOT_PUBLISHED")}
                              </Box>
                            </Box>
                          </StyledTableCell>
                          <StyledTableCell>{getProgress(row)}</StyledTableCell>
                          <StyledTableCell>
                            <Typography variant="caption">
                              {row.start_at}
                            </Typography>
                          </StyledTableCell>
                          <StyledTableCell>
                            <Typography variant="caption">
                              {row.end_at}
                            </Typography>
                          </StyledTableCell>
                          <StyledTableCell>
                            <Box display="flex" justifyContent="flex-end">
                              {row.budget.toLocaleString("en-US", {
                                minimumFractionDigits: exp,
                                maximumFractionDigits: exp,
                              })}
                            </Box>
                          </StyledTableCell>
                          <StyledTableCell>
                            <Box display="flex" justifyContent="flex-end">
                              {row?.credit_used?.toLocaleString("en-US", {
                                minimumFractionDigits: exp,
                                maximumFractionDigits: exp,
                              }) ??  '-'}
                            </Box>
                          </StyledTableCell>
                           <StyledTableCell>
                            <Box display="flex" justifyContent="flex-end">
                              {row?.budget_left?.toLocaleString("en-US", {
                                minimumFractionDigits: exp,
                                maximumFractionDigits: exp,
                              }) ??  '-'}
                            </Box>
                          </StyledTableCell>
                          <StyledTableCell>
                            <Box display="flex" justifyContent="flex-end">
                              {row.win_count}
                            </Box>
                          </StyledTableCell>
                          <StyledTableCell>
                            <Box display="flex" justifyContent="flex-end">
                              {row.claimed_count}
                            </Box>
                          </StyledTableCell>

                          <StickyRightTableCell
                            style={{
                              textAlign: "center",
                              backgroundColor: "inherit",
                            }}
                          >
                            <Box px={1} display="flex" justifyContent="center">
                              {row.status !== "Not Published" ? (
                                <IconButton
                                  size="small"
                                  onClick={() => handleExport(row)}
                                >
                                  <DownloadIcon />
                                </IconButton>
                              ) : (
                                "-"
                              )}
                            </Box>
                          </StickyRightTableCell>
                        </StyledTableRow>
                      );
                    })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            style={{
              backgroundColor: "#fff",
            }}
            ActionsComponent={TablePaginationActions}
            rowsPerPageOptions={paginationOptions}
            component="div"
            count={filteredRows.length}
            rowsPerPage={tableFilter.rowsPerPage}
            page={tableFilter.page}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
            SelectProps={{
              classes: classes.toolbar,
            }}
            labelRowsPerPage={getLang(lang, "label.ROWS_PER_PAGE")}
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} ${getLang(
                lang,
                "label.OF"
              )} ${count.toLocaleString("en-US")}`
            }
          />
        </Box>
      ) : null}
      {!isFetching && !isError && filteredRows.length === 0 && (
        <Box>
          <TableContainer className={classes.tableContainer}>
            <Table
              className={classes.table}
              size="small"
              aria-label="assign table"
              stickyHeader
            >
              <TableHead>
                <TableRow>
                  <StickyLeftTableCell 
                    style={{
                        textAlign: "center",
                        position: "sticky",
                        left: 0,
                        zIndex: 130, 
                        background: "#fff", 
                      }}
                    >
                    <Box px={1}>{getLang(lang, "label.CAMPAIGN_NAME")}</Box>
                  </StickyLeftTableCell>
                  <StyledTableCell>
                    {getLang(lang, "label.ACTIVE_PERIOD")}
                  </StyledTableCell>
                  <StyledTableCell>
                    {getLang(lang, "label.BUDGET")}{" "}
                    {disbursementInfo ? `(${disbursementInfo.currency})` : ""}
                  </StyledTableCell>
                  <StyledTableCell>
                    {getLang(lang, "label.CREDIT_USED")}{" "}
                    {disbursementInfo ? `(${disbursementInfo.currency})` : ""}
                  </StyledTableCell>
                  <StyledTableCell>
                    {getLang(lang, "label.WIN_COUNT")}
                  </StyledTableCell>
                  <StyledTableCell>
                    {getLang(lang, "label.CLAIMED_COUNT")}
                  </StyledTableCell>
                  <StyledTableCell>
                    {getLang(lang, "label.STATUS")}
                  </StyledTableCell>
                  <StickyRightTableCell style={{ textAlign: "center" }}>
                    <Box px={1}>
                      {getLang(lang, "label.DOWNLOAD_REPORT_TITLE")}
                    </Box>
                  </StickyRightTableCell>
                </TableRow>
              </TableHead>
            </Table>
          </TableContainer>
          <Box py={9} className={classes.emptyContainer}>
            <Typography
              variant="body1"
              style={{ paddingBottom: "16px", fontWeight: "bold" }}
            >
              {getLang(lang, "paragraph.NO_CAMPAIGN_ASSOCIATED")}
            </Typography>
          </Box>
        </Box>
      )}
    </>
  );
}
